import { saveStaffSessionToken } from '../staffSession';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { useAppDispatch } from '../../../store';
import { setAdminMode, setAuthToken } from '../state/authSlice';
import { useTeamGoogleLoginMutation } from '../api/authApi';
import { getErrorMessage } from '../../../utils';

const HOSTED_DOMAIN = 'farmstream.co.uk';

/**
 * Google Workspace SSO button for the admin team.
 *
 * - Loads the Google Identity Services (GIS) global from index.html.
 * - Initialises with our REACT_APP_GOOGLE_CLIENT_ID and pins `hd` to the
 *   Farmstream Workspace so personal Google accounts are not offered.
 * - Renders the official Google button and (when no admin session exists)
 *   also fires One Tap for already-signed-in Chrome profiles.
 * - On callback: posts the ID token to /auth/team-google-login, stores the
 *   named staff session, and navigates to the
 *   admin user selection page.
 */
export const GoogleSignInButton = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  // Honour ?next=/some-page (internal paths only) so deep links like
  // /callum survive the SSO round-trip.
  const nextParam = searchParams.get('next');
  const destination =
    nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')
      ? nextParam
      : '/admin/select-user';
  const [teamGoogleLogin] = useTeamGoogleLoginMutation();
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState<string | null>(null);

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!clientId) {
      setHasError('Google SSO is not configured (REACT_APP_GOOGLE_CLIENT_ID missing).');
      return;
    }

    let cancelled = false;
    let pollId: ReturnType<typeof setInterval> | null = null;

    const handleCredential = async (response: CredentialResponse) => {
      try {
        const res = await teamGoogleLogin({ credential: response.credential }).unwrap();
        if (res?.token) {
          saveStaffSessionToken(res.token);
          dispatch(setAuthToken(res.token));
          dispatch(setAdminMode(true));
          toast(`Signed in as ${res.email}`, { type: 'success' });
          navigate(destination);
        } else {
          toast('Unexpected SSO response from server', { type: 'error' });
        }
      } catch (err) {
        const message = getErrorMessage(err, 'Google sign-in failed');
        toast(message, { type: 'error' });
      }
    };

    const init = () => {
      const gsi = window.google?.accounts?.id;
      if (!gsi || !buttonRef.current || cancelled) return false;

      gsi.initialize({
        client_id: clientId,
        callback: handleCredential,
        hd: HOSTED_DOMAIN,
        ux_mode: 'popup',
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      gsi.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: 320,
      });

      // Fire One Tap for already-signed-in Chrome Workspace profiles.
      // Silently no-ops if it can't be shown.
      try {
        gsi.prompt();
      } catch {
        // ignore
      }

      setIsReady(true);
      return true;
    };

    if (!init()) {
      pollId = setInterval(() => {
        if (init() && pollId) {
          clearInterval(pollId);
          pollId = null;
        }
      }, 200);
      // Stop polling after ~6s if the script never loads.
      setTimeout(() => {
        if (pollId) {
          clearInterval(pollId);
          pollId = null;
          if (!cancelled && !isReady) {
            setHasError('Could not load Google Sign-In. Check your network or ad blocker.');
          }
        }
      }, 6000);
    }

    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
      try {
        window.google?.accounts.id.cancel();
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (hasError) {
    return <Hint role="alert">{hasError}</Hint>;
  }

  return (
    <Wrapper>
      <ButtonHost ref={buttonRef} />
      <Hint>Use your @{HOSTED_DOMAIN} Workspace account.</Hint>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const ButtonHost = styled.div`
  min-height: 44px;
  display: flex;
  justify-content: center;
`;

const Hint = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
`;
